import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Save,
  Users,
  Loader2,
  Building2,
  Briefcase,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/services/apiService";
import { createUser, updateUser, type User } from "@/services/usersService";
import { validatePasswordStrength } from "@/utils/passwordValidator";
import { listClients, type Client } from "@/services/clientsService";
import { listWorkspaces } from "@/services/workspacesService";
import { Workspace } from "@/types/workspace";
import { listProfiles, type Profile } from "@/services/profilesService";
import { usePermissions } from "@/hooks/usePermissions";

interface UserFormProps {
  onBack: () => void;
  onSave: () => void;
  editingUser: User | null;
}

interface FormData {
  name: string;
  email: string;
  accessTypeId: number;
  profileId: string;
  password: string;
  clientIds: string[];
  workspaceIds: string[];
}

export const UserForm = ({ onBack, onSave, editingUser }: UserFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    accessTypeId: 0, // Será definido após carregar tipos de acesso
    profileId: "",
    password: "",
    clientIds: [],
    workspaceIds: [],
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  const [accessTypes, setAccessTypes] = useState<
    Array<{ id: number; name: string; is_active: boolean }>
  >([]);
  const [passwordAccessTypeId, setPasswordAccessTypeId] = useState<
    number | null
  >(null);
  const [googleAccessTypeId, setGoogleAccessTypeId] = useState<number | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [emailError, setEmailError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { isAdmin } = usePermissions();

  useEffect(() => {
    loadFormData();
  }, [editingUser]);

  const loadFormData = async () => {
    setLoading(true);
    try {
      // Buscar configurações de autenticação e tipos de acesso
      const [authConfigResponse, accessTypesResponse] = await Promise.all([
        apiRequest<{ data: { googleOAuthEnabled: boolean } }>(
          "/auth/config",
          "GET"
        ),
        apiRequest<{
          data: Array<{ id: number; name: string; is_active: boolean }>;
        }>("/types/user-access", "GET"),
      ]);

      setGoogleOAuthEnabled(authConfigResponse.data.googleOAuthEnabled);

      // Processar tipos de acesso
      if (accessTypesResponse.success && accessTypesResponse.data) {
        const types = accessTypesResponse.data;
        setAccessTypes(types);

        // Encontrar IDs reais dos tipos de acesso
        const passwordType = types.find(
          (t) => t.name === "password" && t.is_active
        );
        const googleType = types.find(
          (t) => t.name === "google" && t.is_active
        );

        if (passwordType) {
          setPasswordAccessTypeId(passwordType.id);
        }
        if (googleType) {
          setGoogleAccessTypeId(googleType.id);
        }
      }

      // Buscar em paralelo
      const [clientsResponse, workspacesResponse, profilesResponse] =
        await Promise.all([
          listClients({ limit: 100,status_id: 1 }),
          listWorkspaces({ limit: 100 }),
          listProfiles({ limit: 100, is_active: true }),
        ]);

      if (clientsResponse.success && clientsResponse.data) {
        const clientsArray = (clientsResponse.data as any).clients || [];
        setClients(Array.isArray(clientsArray) ? clientsArray : []);
      }

      if (workspacesResponse.success && workspacesResponse.data) {
        const workspacesArray = (workspacesResponse.data as any).data || [];
        setWorkspaces(Array.isArray(workspacesArray) ? workspacesArray : []);
      }

      if (profilesResponse.success && profilesResponse.data) {
        const profilesArray = profilesResponse.data.data || [];
        setProfiles(Array.isArray(profilesArray) ? profilesArray : []);
      }

      // Aguardar processar tipos de acesso antes de definir formData
      let passwordId: number | null = null;
      let googleId: number | null = null;

      if (accessTypesResponse.success && accessTypesResponse.data) {
        const passwordType = accessTypesResponse.data.find(
          (t) => t.name === "password" && t.is_active
        );
        const googleType = accessTypesResponse.data.find(
          (t) => t.name === "google" && t.is_active
        );
        passwordId = passwordType?.id || null;
        googleId = googleType?.id || null;
      }

      if (editingUser) {
        const userClientIds = editingUser.clients?.map((c) => c.id) || [];
        const userWorkspaceIds = editingUser.workspaces?.map((w) => w.id) || [];

        setFormData({
          name: editingUser.name || "",
          email: editingUser.email || "",
          accessTypeId: editingUser.access_type_id || passwordId || 0,
          profileId: editingUser.profile_id || "",
          password: "",
          clientIds: userClientIds,
          workspaceIds: userWorkspaceIds,
        });
        setSelectedClientIds(userClientIds);
      } else {
        // Se Google OAuth estiver habilitado, definir como padrão (google)
        // Caso contrário, usar password como padrão
        const defaultAccessTypeId =
          authConfigResponse.data.googleOAuthEnabled && googleId
            ? googleId
            : passwordId || 0;
        setFormData((prev) => ({ ...prev, accessTypeId: defaultAccessTypeId }));
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validações
    if (!formData.name.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Nome é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        title: "Erro de Validação",
        description: "Email é obrigatório",
        variant: "destructive",
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Erro de Validação",
        description: "Email inválido",
        variant: "destructive",
      });
      return;
    }

    if (!formData.profileId) {
      toast({
        title: "Erro de Validação",
        description: "Selecione um perfil de acesso",
        variant: "destructive",
      });
      return;
    }

    // Senha é opcional - se não fornecida, será gerada automaticamente e enviada por e-mail

    if (formData.password && formData.accessTypeId === passwordAccessTypeId) {
      const passwordValidation = validatePasswordStrength(formData.password);
      if (!passwordValidation.isValid) {
        toast({
          title: "Erro de Validação",
          description: passwordValidation.errors.join(". "),
          variant: "destructive",
        });
        return;
      }
    }

    if (formData.clientIds.length === 0) {
      toast({
        title: "Erro de Validação",
        description: "Selecione pelo menos um cliente",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData: any = {
          name: formData.name,
          email: formData.email,
          access_type_id: formData.accessTypeId,
          profile_id: formData.profileId,
          client_ids: formData.clientIds,
          workspace_ids: formData.workspaceIds,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        const response = await updateUser(editingUser.id, updateData);

        if (response.success) {
          toast({
            title: "Sucesso",
            description: "Usuário atualizado com sucesso",
          });
          onSave();
        } else if (response.error?.code === "EMAIL_ALREADY_EXISTS") {
          const message = "Este e-mail já está cadastrado no sistema.";
          setEmailError(message);
          // focar e selecionar para facilitar correção
          if (emailRef.current) {
            emailRef.current.focus();
            emailRef.current.select?.();
          }
          toast({
            title: "E-mail em uso",
            description: message,
            variant: "destructive",
          });
          return;
        } else {
          throw new Error(
            response.error?.message || "Erro ao atualizar usuário"
          );
        }
      } else {
        const createData: any = {
          name: formData.name,
          email: formData.email,
          access_type_id: formData.accessTypeId,
          profile_id: formData.profileId,
          client_ids: formData.clientIds,
          workspace_ids: formData.workspaceIds,
        };

        if (formData.accessTypeId === passwordAccessTypeId) {
          if (formData.password?.trim()) {
            createData.password = formData.password;
          }
        }

        const response = await createUser(createData);

        if (response.success) {
          toast({
            title: "Sucesso",
            description:
              formData.accessTypeId === passwordAccessTypeId
                ? "Usuário criado. Um e-mail de boas-vindas foi enviado para verificar o endereço e criar a senha."
                : "Usuário criado com sucesso",
          });
          onSave();
        } else if (response.error?.code === "EMAIL_ALREADY_EXISTS") {
          const message = "Este e-mail já está cadastrado no sistema.";
          setEmailError(message);
          if (emailRef.current) {
            emailRef.current.focus();
            emailRef.current.select?.();
          }
          toast({
            title: "E-mail em uso",
            description: message,
            variant: "destructive",
          });
          return;
        } else {
          throw new Error(response.error?.message || "Erro ao criar usuário");
        }
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClientToggle = (clientId: string, checked: boolean) => {
    if (checked) {
      setSelectedClientIds([...selectedClientIds, clientId]);
      setFormData({
        ...formData,
        clientIds: [...formData.clientIds, clientId],
      });
    } else {
      setSelectedClientIds(selectedClientIds.filter((id) => id !== clientId));
      // Remover workspaces deste cliente
      const clientWorkspaces = workspaces
        .filter((w) => w.client_id === clientId)
        .map((w) => w.id);
      setFormData({
        ...formData,
        clientIds: formData.clientIds.filter((id) => id !== clientId),
        workspaceIds: formData.workspaceIds.filter(
          (id) => !clientWorkspaces.includes(id)
        ),
      });
    }
  };

  const handleWorkspaceToggle = (
    clientId: string,
    workspaceId: string,
    checked: boolean
  ) => {
    if (checked) {
      setFormData({
        ...formData,
        workspaceIds: [...formData.workspaceIds, workspaceId],
      });
    } else {
      setFormData({
        ...formData,
        workspaceIds: formData.workspaceIds.filter((id) => id !== workspaceId),
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando formulário...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Users}
        title={editingUser ? "Editar Usuário" : "Novo Usuário"}
        description={
          editingUser
            ? "Modifique as informações e permissões do usuário"
            : "Cadastre um novo usuário no sistema"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar Usuário
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Básicas</CardTitle>
              <CardDescription>
                Configure os dados principais do usuário
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Nome completo"
                    disabled={saving}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    ref={emailRef}
                    aria-invalid={!!emailError}
                    className={emailError ? "border-red-500 focus-visible:ring-red-500" : undefined}
                    value={formData.email}
                    onChange={(e) => {
                      if (emailError) setEmailError(null);
                      setFormData({ ...formData, email: e.target.value });
                    }}
                    placeholder="email@mail.com"
                    disabled={saving || (editingUser && !isAdmin)}
                  />
                  {emailError && (
                    <p className="text-xs text-red-600">{emailError}</p>
                  )}
                  {editingUser && !isAdmin && (
                    <p className="text-xs text-muted-foreground">
                      Apenas administradores podem alterar o e-mail
                    </p>
                  )}
                </div>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="profile">Perfil de Acesso *</Label>
                <Select
                  value={formData.profileId || undefined}
                  onValueChange={(value) =>
                    setFormData({ ...formData, profileId: value })
                  }
                  disabled={saving}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  O perfil define as permissões do usuário no sistema
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Access Type */}
          <Card>
            <CardHeader>
              <CardTitle>Tipo de Acesso</CardTitle>
              <CardDescription>
                Configure como o usuário fará login no sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {googleOAuthEnabled && googleAccessTypeId ? (
                <RadioGroup
                  value={formData.accessTypeId.toString()}
                  onValueChange={(value) =>
                    setFormData({ ...formData, accessTypeId: parseInt(value) })
                  }
                  disabled={saving}
                >
                  {googleAccessTypeId && (
                    <div className="flex items-center space-x-2 p-3 border rounded-lg">
                      <RadioGroupItem
                        value={googleAccessTypeId.toString()}
                        id="google"
                      />
                      <Label htmlFor="google" className="flex-1 cursor-pointer">
                        Acesso via Google
                      </Label>
                    </div>
                  )}
                  {passwordAccessTypeId && (
                    <div className="flex items-center space-x-2 p-3 border rounded-lg">
                      <RadioGroupItem
                        value={passwordAccessTypeId.toString()}
                        id="password"
                      />
                      <Label
                        htmlFor="password"
                        className="flex-1 cursor-pointer"
                      >
                        Acesso via usuário e senha
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              ) : (
                <div className="p-3 border rounded-lg bg-muted/50">
                  <p className="text-sm font-medium">
                    Acesso via usuário e senha
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Login via Google está desabilitado no sistema
                  </p>
                </div>
              )}

              {passwordAccessTypeId &&
                formData.accessTypeId === passwordAccessTypeId && (
                  <div className="grid gap-2">
                    <Label htmlFor="password">
                      Senha {!editingUser && "(opcional)"}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder={
                        editingUser
                          ? "Deixe em branco para manter a senha atual"
                          : "Deixe em branco — o usuário cria a senha pelo e-mail"
                      }
                      disabled={saving}
                    />
                    {editingUser && (
                      <p className="text-xs text-muted-foreground">
                        Deixe em branco para manter a senha atual
                      </p>
                    )}
                    {!editingUser && (
                      <p className="text-xs text-muted-foreground">
                        Se não informada, o usuário recebe um e-mail único para
                        verificar o endereço e criar a própria senha (link válido
                        por 72 horas). Nenhuma senha temporária é enviada.
                        {formData.password &&
                          " A senha deve ter pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas, números e caracteres especiais."}
                      </p>
                    )}
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Clients and Workspaces */}
          <Card>
            <CardHeader>
              <CardTitle>Clientes e Workspaces</CardTitle>
              <CardDescription>
                Selecione os clientes e workspaces que o usuário terá acesso
              </CardDescription>
            </CardHeader>
            <CardContent>
              {clients.length > 0 ? (
                <div className="space-y-4">
                  {clients.map((client) => {
                    const clientWorkspaces = workspaces.filter(
                      (w) => w.client_id === client.id
                    );
                    const isClientSelected = selectedClientIds.includes(
                      client.id
                    );

                    return (
                      <div
                        key={client.id}
                        className="border rounded-lg p-4 space-y-3"
                      >
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={`client-${client.id}`}
                            checked={isClientSelected}
                            onCheckedChange={(checked) =>
                              handleClientToggle(client.id, checked as boolean)
                            }
                            disabled={saving}
                          />
                          <Label
                            htmlFor={`client-${client.id}`}
                            className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-1"
                          >
                            <Building2 className="h-4 w-4" />
                            {client.company}
                          </Label>
                        </div>

                        {isClientSelected && clientWorkspaces.length > 0 && (
                          <div className="ml-6 pl-4 border-l-2 space-y-2">
                            <p className="text-xs text-muted-foreground mb-2">
                              Workspaces disponíveis:
                            </p>
                            {clientWorkspaces.map((workspace) => (
                              <div
                                key={workspace.id}
                                className="flex items-center space-x-2"
                              >
                                <Checkbox
                                  id={`workspace-${workspace.id}`}
                                  checked={formData.workspaceIds.includes(
                                    workspace.id
                                  )}
                                  onCheckedChange={(checked) =>
                                    handleWorkspaceToggle(
                                      client.id,
                                      workspace.id,
                                      checked as boolean
                                    )
                                  }
                                  disabled={saving}
                                />
                                <Label
                                  htmlFor={`workspace-${workspace.id}`}
                                  className="text-sm cursor-pointer flex items-center gap-2 flex-1"
                                >
                                  <Briefcase className="h-3 w-3" />
                                  {workspace.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {formData.clientIds.length > 0 && (
                    <div className="mt-4 flex items-center space-x-2">
                      <Badge variant="secondary">
                        {formData.clientIds.length} cliente
                        {formData.clientIds.length !== 1 ? "s" : ""} selecionado
                        {formData.clientIds.length !== 1 ? "s" : ""}
                      </Badge>
                      {formData.workspaceIds.length > 0 && (
                        <Badge variant="outline">
                          {formData.workspaceIds.length} workspace
                          {formData.workspaceIds.length !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    Nenhum cliente disponível. Cadastre clientes primeiro.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};
